/**
 * ChatBubble — a floating, always-available chat pop-out.
 *
 * A soft-glow bubble parks in the bottom-right corner. Clicking it expands
 * a compact 340×480 window with World / Clan / Whisper tabs. It reuses the
 * same GameContext chat stream + `/api/game/social/chat/*` endpoints as the
 * full ChatPanel, so anything typed here surfaces everywhere.
 *
 * Design goals: friendly, encouraging, low-friction. We surface presence
 * ("N wanderers online"), unread badges, and quick access to whispers so
 * jumping into the world's conversation is one tap away.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  MessageCircle, X, Send, MessageSquare, Shield, Users, Sparkles, Circle, Flag,
} from "lucide-react";

import { api, formatApiError } from "@/lib/api";
import { flagUrl } from "@/lib/utils";
import { useGame } from "@/context/GameContext";
import { useOpenPlayer } from "@/components/game/PlayerCard";

const CHANNELS = [
  { id: "world",   label: "World",   icon: MessageSquare, hint: "Everyone in Territoria." },
  { id: "country", label: "Country", icon: Flag,          hint: "Only your homeland." },
  { id: "clan",    label: "Clan",    icon: Shield,        hint: "Only your clan." },
  { id: "dm",      label: "Whisper", icon: Users,         hint: "One-on-one with a friend." },
];

function TimeStamp({ iso }) {
  const t = new Date(iso);
  return (
    <span className="text-[9px] font-numeric text-stone-600 shrink-0" title={iso}>
      {t.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}
    </span>
  );
}

export function ChatBubble() {
  const { chat, online, sendChat, snap } = useGame();
  const openPlayer = useOpenPlayer();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("world");
  const [text, setText] = useState("");
  const [dmTarget, setDmTarget] = useState(null);
  const [dmList, setDmList] = useState([]);
  const [historyByChannel, setHistoryByChannel] = useState({});
  const [lastSeenCount, setLastSeenCount] = useState(chat.length);
  const scrollRef = useRef(null);

  const key = channel === "dm" && dmTarget ? `dm:${dmTarget.id}` : channel;

  // Load friends for whispers.
  useEffect(() => {
    api.get("/api/game/social/friends")
      .then(({ data }) => setDmList(data.friends))
      .catch(() => {});
  }, []);

  // Fetch recent messages for the selected channel when it changes / opens.
  const loadRecent = useCallback(async () => {
    if (!open) return;
    try {
      const { data } = await api.get(`/api/game/social/chat/${encodeURIComponent(key)}`);
      setHistoryByChannel((p) => ({ ...p, [key]: data.messages }));
    } catch (e) {
      if (channel !== "clan") toast.error(formatApiError(e));
    }
  }, [open, key, channel]);
  useEffect(() => { loadRecent(); }, [loadRecent]);

  // Merge stored + live world chat, filtered by channel.
  const merged = useMemo(() => {
    const stored = historyByChannel[key] || [];
    const live = chat.filter((m) => {
      if (channel === "world")   return !m.channel || m.channel === "world";
      if (channel === "country") return m.channel === `country:${snap?.home_country_iso}`;
      if (channel === "clan")    return m.channel === "clan" || m.channel?.startsWith("clan:");
      if (channel === "dm" && dmTarget) return m.channel === `dm:${dmTarget.id}` || m.channel === `dm:${snap?.user_id}`;
      return false;
    });
    const seen = new Set(stored.map((m) => `${m.author_id || m.author}-${m.created_at}`));
    const combined = [...stored];
    for (const m of live) {
      const id = `${m.author_id || m.author}-${m.created_at}`;
      if (!seen.has(id)) combined.push(m);
    }
    return combined;
  }, [chat, historyByChannel, key, channel, dmTarget, snap?.user_id]);

  // Auto-scroll to bottom on new messages.
  useEffect(() => {
    if (!open) return;
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [merged.length, open]);

  // Unread badge = number of world messages since bubble was last opened.
  useEffect(() => {
    if (open) setLastSeenCount(chat.length);
  }, [open, chat.length]);
  const unread = Math.max(0, chat.length - lastSeenCount);

  const submit = async (e) => {
    e?.preventDefault?.();
    const t = text.trim();
    if (!t) return;
    setText("");
    try {
      await sendChat(t, channel === "dm" && dmTarget ? `dm:${dmTarget.id}` : channel);
      // Optimistically refetch stored history after a beat so we see the DM/clan copies.
      setTimeout(loadRecent, 300);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  // ------ Trigger bubble --------------------------------------------------

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid="chat-bubble-open"
        aria-label="Open chat"
        className="fixed z-40 bottom-4 right-4 group flex items-center gap-2 pl-3 pr-4 py-2.5 rounded-full border border-amber-700/60 bg-stone-950/95 backdrop-blur-md shadow-[0_10px_40px_-10px_rgba(217,126,108,0.55)] hover:border-amber-500 hover:shadow-[0_12px_40px_-6px_rgba(217,126,108,0.75)] transition-all"
      >
        <span className="relative flex items-center justify-center">
          <span className="absolute inset-0 rounded-full bg-amber-500/30 blur-md group-hover:bg-amber-400/40 transition-colors" />
          <MessageCircle size={18} className="relative text-amber-300" strokeWidth={1.6} />
          {unread > 0 && (
            <span
              className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 border border-stone-950 text-[10px] font-numeric text-white flex items-center justify-center animate-pulse"
              data-testid="chat-bubble-unread"
            >
              {unread > 9 ? "9+" : unread}
            </span>
          )}
        </span>
        <span className="text-xs font-body text-amber-200 group-hover:text-amber-100 transition-colors hidden sm:inline">
          Chat
        </span>
        <span className="text-[10px] font-numeric text-stone-500 border-l border-stone-800 pl-2 hidden sm:inline">
          <Circle size={6} className="inline fill-emerald-400 text-emerald-400 mr-1" /> {online ?? 0}
        </span>
      </button>
    );
  }

  // ------ Expanded pop-out ------------------------------------------------

  return (
    <div
      className="fixed z-40 bottom-4 right-4 w-[min(360px,calc(100vw-2rem))] h-[min(520px,calc(100vh-2rem))] rounded-2xl border border-amber-800/60 bg-stone-950/97 backdrop-blur-md shadow-[0_20px_60px_-10px_rgba(0,0,0,0.7)] flex flex-col overflow-hidden"
      data-testid="chat-bubble-window"
    >
      {/* Header */}
      <div className="flex items-center gap-2 px-3 py-2 border-b border-stone-800 bg-gradient-to-b from-stone-900/80 to-transparent">
        <div className="w-8 h-8 rounded-full bg-amber-950/50 border border-amber-800/70 flex items-center justify-center">
          <Sparkles size={14} className="text-amber-300" strokeWidth={1.6} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-xs font-display heading-gilded leading-none">Territoria Chat</div>
          <div className="text-[10px] font-numeric text-stone-500 mt-0.5 flex items-center gap-1">
            <Circle size={6} className="fill-emerald-400 text-emerald-400" /> {online ?? 0} wanderers online
          </div>
        </div>
        <button
          onClick={() => setOpen(false)}
          className="text-stone-500 hover:text-amber-300 transition-colors"
          aria-label="Close chat"
          data-testid="chat-bubble-close"
        >
          <X size={16} />
        </button>
      </div>

      {/* Channel tabs */}
      <div className="flex items-center gap-1 px-2 py-1.5 border-b border-stone-800">
        {CHANNELS.map((c) => {
          const Icon = c.icon;
          const active = channel === c.id;
          return (
            <button
              key={c.id}
              onClick={() => { setChannel(c.id); if (c.id !== "dm") setDmTarget(null); }}
              className={
                "flex-1 flex items-center justify-center gap-1 text-[11px] font-body py-1.5 rounded-md transition-colors " +
                (active
                  ? "bg-amber-900/40 text-amber-100 border border-amber-700/60"
                  : "text-stone-400 hover:text-amber-200 hover:bg-stone-900/60 border border-transparent")
              }
              data-testid={`chat-bubble-tab-${c.id}`}
              title={c.hint}
            >
              <Icon size={12} strokeWidth={1.7} /> {c.label}
            </button>
          );
        })}
      </div>

      {/* DM picker */}
      {channel === "dm" && !dmTarget && (
        <div className="flex-1 overflow-y-auto p-3 scroll-thin">
          <div className="text-[10px] font-numeric uppercase tracking-widest text-stone-500 mb-2">Whisper a friend</div>
          {dmList.length === 0 ? (
            <div className="text-stone-500 italic text-xs font-body py-4 text-center">
              Add friends from the Friends panel to start whispering.
            </div>
          ) : (
            <div className="space-y-1">
              {dmList.map((f) => (
                <button
                  key={f.id}
                  onClick={() => setDmTarget({ id: f.id, name: f.name })}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded border border-stone-800 hover:border-amber-800 hover:bg-stone-900/60 transition-colors text-left"
                  data-testid={`chat-bubble-dm-${f.id}`}
                >
                  <div className="w-6 h-6 rounded-full bg-stone-950 border border-amber-800/60 flex items-center justify-center font-display text-[9px] text-amber-300">
                    {(f.name || "?").slice(0, 2).toUpperCase()}
                  </div>
                  <span className="text-sm font-body text-stone-200 truncate flex-1">{f.name}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Message list */}
      {(channel !== "dm" || dmTarget) && (
        <div
          ref={scrollRef}
          className="flex-1 overflow-y-auto scroll-thin p-3 space-y-1.5"
          data-testid="chat-bubble-messages"
        >
          {channel === "dm" && dmTarget && (
            <button
              onClick={() => setDmTarget(null)}
              className="text-[10px] font-numeric uppercase tracking-widest text-stone-500 hover:text-amber-300 mb-1"
            >
              ← switch friend
            </button>
          )}

          {merged.length === 0 ? (
            <div className="text-stone-500 italic text-xs font-body py-10 text-center">
              {channel === "world" && "Be the first to say hello — the world is listening."}
              {channel === "clan"  && "Rally your clan. Every campaign starts with a whisper."}
              {channel === "dm"    && `Say hi to ${dmTarget?.name || "your friend"}!`}
            </div>
          ) : (
            merged.map((m, i) => (
              <div key={i} className="flex items-start gap-1.5 text-sm font-body" data-testid={`chat-bubble-msg-${i}`}>
                <TimeStamp iso={m.created_at} />
                {m.author_iso_a2 && (
                  <img
                    src={flagUrl(m.author_iso_a2, 20)}
                    alt={m.author_iso_a3 || m.author_iso_a2}
                    className="w-3.5 h-2.5 object-cover rounded-[1px] shadow-[0_0_0_1px_rgba(0,0,0,0.6)] mt-[3px] shrink-0"
                  />
                )}
                <button
                  type="button"
                  onClick={() => m.author_id && openPlayer(m.author_id)}
                  disabled={!m.author_id}
                  className={
                    "font-numeric shrink-0 " +
                    (m.author_id
                      ? "text-amber-400 hover:text-amber-200 hover:underline underline-offset-2 cursor-pointer"
                      : "text-amber-400")
                  }
                >
                  {m.author}:
                </button>
                <span className="text-stone-200 break-words min-w-0">{m.text}</span>
              </div>
            ))
          )}
        </div>
      )}

      {/* Composer */}
      {(channel !== "dm" || dmTarget) && (
        <form
          onSubmit={submit}
          className="border-t border-stone-800 p-2 flex items-center gap-1.5 bg-stone-950/60"
        >
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={
              channel === "world"   ? "Say something to the world…" :
              channel === "country" ? `Message ${snap?.home_country_name || "your country"}…` :
              channel === "clan"    ? "Message your clan…" :
              `Whisper ${dmTarget?.name || "a friend"}…`
            }
            maxLength={280}
            className="flex-1 bg-stone-900 border border-stone-700 focus:border-amber-600 rounded-md px-2.5 py-1.5 text-sm font-body text-stone-100 outline-none placeholder:text-stone-600"
            data-testid="chat-bubble-input"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="w-8 h-8 flex items-center justify-center rounded-md bg-amber-700 hover:bg-amber-600 text-stone-950 disabled:bg-stone-800 disabled:text-stone-600 transition-colors"
            aria-label="Send"
            data-testid="chat-bubble-send"
          >
            <Send size={14} />
          </button>
        </form>
      )}
    </div>
  );
}
